import type { DailyCall, DailyParticipant } from "@daily-co/daily-js";
import { useEffect, useRef, useState } from "react";

import type { ConversationState } from "../lib/types";

interface Props {
  callObject: DailyCall | null;
  connected: boolean;
  status: ConversationState;
  camOn: boolean;
}

/**
 * Àdùnní's video, with the local camera inset.
 *
 * Uses a Daily call object rather than the prebuilt iframe so the layout is
 * ours and the app-message stream stays readable — that stream is how barge-in
 * reaches the backend.
 */
export function VideoStage({ callObject, connected, status, camOn }: Props) {
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [remoteJoined, setRemoteJoined] = useState(false);

  useEffect(() => {
    if (!callObject) return;

    const attach = () => {
      const participants = callObject.participants();
      const remote = Object.values(participants).find(
        (p: DailyParticipant) => !p.local,
      );

      if (remote) {
        setRemoteJoined(true);
        const video = remote.tracks?.video?.persistentTrack;
        const audio = remote.tracks?.audio?.persistentTrack;
        if (video && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = new MediaStream([video]);
        }
        if (audio && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = new MediaStream([audio]);
        }
      }

      const localTrack = participants.local?.tracks?.video?.persistentTrack;
      if (localTrack && localVideoRef.current) {
        localVideoRef.current.srcObject = new MediaStream([localTrack]);
      }
    };

    const events = [
      "participant-joined",
      "participant-updated",
      "participant-left",
      "track-started",
      "track-stopped",
      "joined-meeting",
    ] as const;

    events.forEach((event) => callObject.on(event, attach));
    attach();

    return () => {
      events.forEach((event) => callObject.off(event, attach));
    };
  }, [callObject]);

  const speaking = status === "speaking";

  return (
    <div className={`stage ${speaking ? "stage--speaking" : ""}`}>
      <video
        ref={remoteVideoRef}
        className="stage__remote"
        autoPlay
        playsInline
        muted
      />
      {/* Audio is a separate element so muting the video tag never mutes her. */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      {(!connected || !remoteJoined) && (
        <div className="stage__placeholder">
          <div className="stage__avatar" aria-hidden="true">
            À
          </div>
          <p className="stage__placeholder-text">
            {connected ? "Àdùnní is joining…" : "Not connected"}
          </p>
        </div>
      )}

      <div className={`stage__self ${camOn ? "" : "stage__self--off"}`}>
        <video ref={localVideoRef} autoPlay playsInline muted />
        {!camOn && <span className="stage__self-off">Camera off</span>}
      </div>
    </div>
  );
}
